import React from "react";
import { Alert, Card, CardBody, Col, Container, Row, Spinner } from "reactstrap";
import { useParams } from "react-router-dom";

import CharacterAvatar from "../../../Components/Common/CharacterAvatar";
import { getBattle } from "../../../helpers/api/battle";
import { withStableBattleParticipations } from "../../../helpers/battle/participations";
import { useAuthStore } from "../../../store/useAuthStore";
import Arena from "../../../Components/Common/Arena";
import Dashboard, { BattleLike, BetLike, ParticipationUi } from "../Dashboard";

const ACTIVE_STATUSES = ["active"];
const CANCELLED_STATUSES = ["cancelled", "canceled"];

const formatPoints = (value?: number | null) => {
  if (value == null || !Number.isFinite(Number(value))) return "";
  return new Intl.NumberFormat("en-US").format(Math.round(Number(value)));
};

const getParticipationScore = (participation: ParticipationUi, fallback: number) => {
  const value =
    participation.score ??
    participation.pool.totalVolume ??
    fallback;

  return Number.isFinite(Number(value)) ? Number(value) : fallback;
};

const getWinnerParticipationId = (battle: BattleLike, participations: ParticipationUi[]) => {
  const explicitWinnerId = battle.winnerParticipationId;

  if (explicitWinnerId != null) {
    const matched = participations.find((participation) => {
      const character = participation.character ?? {};
      return [participation.id, character.id].some(
        (candidate) => String(candidate) === String(explicitWinnerId)
      );
    });

    if (matched) return matched.id;
  }

  const flaggedWinner = participations.find((participation) => {
    const result = String(participation.result ?? "").toLowerCase();
    return participation.isWinner === true || result === "winner" || result === "won";
  });

  if (flaggedWinner) return flaggedWinner.id;

  const [left, right] = participations;
  if (!left || !right) return undefined;

  const leftScore = getParticipationScore(left, 0);
  const rightScore = getParticipationScore(right, 0);
  if (leftScore === rightScore) return undefined;

  return leftScore > rightScore ? left.id : right.id;
};

const getUserBets = (bets: BetLike[] | undefined, userId?: string | number) => {
  if (!userId) return [];

  return (bets ?? []).filter((bet) => {
    const status = String(bet.status ?? "").toLowerCase();
    return String(bet.userId) === String(userId) && status !== "cancelled" && status !== "canceled";
  });
};

const getParticipationResult = (
  participation: ParticipationUi,
  oppositeParticipation: ParticipationUi,
  userBets: BetLike[],
  winnerParticipationId?: string
) => {
  const currentPool = participation.pool.totalVolume;
  const oppositePool = oppositeParticipation.pool.totalVolume;
  const participationBets = userBets.filter((bet) => String(bet.participationId) === String(participation.id));
  const staked = participationBets.reduce((total, bet) => total + Number(bet.stakedPoints || 0), 0);
  const share = staked > 0 && currentPool > 0 ? (staked / currentPool) * 100 : null;
  const isWinner = winnerParticipationId != null && String(participation.id) === String(winnerParticipationId);
  const profit = isWinner && share != null ? Math.round((staked / currentPool) * oppositePool) : null;
  const calculatedPayout = isWinner ? staked + (profit ?? 0) : 0;
  const explicitPayout = participationBets.reduce((total, bet) => {
    const value = Number(bet.payoutPoints);
    return Number.isFinite(value) && value > 0 ? total + value : total;
  }, 0);
  const payout = staked > 0 ? explicitPayout || calculatedPayout : null;

  return {
    isWinner,
    pool: currentPool,
    profit,
    payout,
    share,
    staked,
  };
};

const ResultAvatar = ({
  participation,
  side,
  winner,
}: {
  participation: ParticipationUi;
  side: "left" | "right";
  winner: boolean;
}) => {
  return (
    <div
      className={`fighter-portrait-card d-inline-flex flex-column align-items-center gap-2 battle-result-avatar battle-result-avatar--${side}`}
      data-cy={`battle-result-avatar-${participation.id}`}
    >
      <div className="battle-result-avatar__image result-fighter-image">
        {winner && <span className="battle-result-winner-tag">Winner</span>}
        <CharacterAvatar
          avatar={participation.character.avatar}
          fallbackClassName="fighter-portrait fighter-portrait--fallback rounded-circle"
          imageClassName="fighter-portrait img-fluid rounded-circle"
          imageStyle={{ maxWidth: 140 }}
          name={participation.character.name}
        />
      </div>
      <strong>{participation.character.name}</strong>
    </div>
  );
};

const ResultBetCard = ({
  participation,
  oppositeParticipation,
  sideLabel,
  tone,
  userBets,
  winnerParticipationId,
}: {
  participation: ParticipationUi;
  oppositeParticipation: ParticipationUi;
  sideLabel: string;
  tone: "cyan" | "purple";
  userBets: BetLike[];
  winnerParticipationId?: string;
}) => {
  const result = getParticipationResult(participation, oppositeParticipation, userBets, winnerParticipationId);
  const hasBet = result.staked > 0;

  return (
    <Card
      className={`battle-result-card battle-result-card--${result.isWinner ? "winner" : "loser"} battle-result-card--${tone}`}
      data-cy={`battle-result-card-${participation.id}`}
    >
      <CardBody>
        <div className="battle-result-card__header">
          <div>
            <div className="battle-result-side">{sideLabel} - {participation.character.name}</div>
          </div>
          <span className="battle-result-outcome" data-cy={`battle-result-outcome-${participation.id}`}>
            {result.isWinner ? "Won" : "Lost"}
          </span>
        </div>

        <dl className="battle-result-lines">
          <div>
            <dt>Your bet</dt>
            <dd data-cy={`battle-result-stake-${participation.id}`}>{hasBet ? `${formatPoints(result.staked)} PTS` : ""}</dd>
          </div>
          <div>
            <dt>Your share</dt>
            <dd data-cy={`battle-result-share-${participation.id}`}>{hasBet && result.share != null ? `${result.share.toFixed(2)} %` : ""}</dd>
          </div>
          <div>
            <dt>{result.isWinner ? "Opponent pool profit" : "Profit"}</dt>
            <dd data-cy={`battle-result-profit-${participation.id}`}>{hasBet && result.profit != null ? `+${formatPoints(result.profit)} PTS` : ""}</dd>
          </div>
          <div>
            <dt>Payout</dt>
            <dd data-cy={`battle-result-payout-${participation.id}`}>{hasBet && result.payout != null ? `${formatPoints(result.payout)} PTS` : ""}</dd>
          </div>
        </dl>

        <div className="battle-result-balance" data-cy={`battle-result-balance-${participation.id}`}>
          <span>{result.isWinner ? "Credited to balance" : "Lost from balance"}</span>
          <strong>
            {hasBet
              ? result.isWinner
                ? `+${formatPoints(result.payout)} PTS`
                : `-${formatPoints(result.staked)} PTS`
              : ""}
          </strong>
        </div>
      </CardBody>
    </Card>
  );
};

const BattleResultView = ({ battle }: { battle: BattleLike }) => {
  document.title = "Legends | The Colosseum - Battle";

  const currentUserId = useAuthStore((state) => state.user?.id);
  const participations = React.useMemo(() => (withStableBattleParticipations(battle).participations ?? []).slice(0, 2), [battle]);
  const [left, right] = participations;
  const winnerParticipationId = getWinnerParticipationId(battle, participations);
  const userBets = getUserBets(battle.bets, currentUserId);
  const leftResult = getParticipationResult(left, right, userBets, winnerParticipationId);
  const rightResult = getParticipationResult(right, left, userBets, winnerParticipationId);
  const totalStaked = leftResult.staked + rightResult.staked;
  const totalReturned = (leftResult.payout ?? 0) + (rightResult.payout ?? 0);
  const net = totalStaked > 0 ? totalReturned - totalStaked : null;
  const winner = participations.find((participation) => String(participation.id) === String(winnerParticipationId));
  const loser = participations.find((participation) => String(participation.id) !== String(winnerParticipationId));

  return (
    <div className="page-content battle-result-page" data-cy="battle-result-page">
      <Container fluid>
        <Row className="g-4 align-items-center mb-4 animate-fade-in battle-result-media-row">
          <Col xs={12} md={3} className="text-center">
            <ResultAvatar participation={left} side="left" winner={String(left.id) === String(winnerParticipationId)} />
          </Col>

          <Col xs={12} md={6} className="text-center">
            <Card className="battle-countdown-card cyber-glass-elevated border-0 overflow-hidden">
              <CardBody className="p-0 position-relative">
                <div className="battle-countdown-frame ratio ratio-21x9" data-cy="battle-result-video">
                  <Arena selectedBattle={battle} variant="slot" />
                </div>
              </CardBody>
            </Card>
            {winner && loser && (
              <div className="battle-result-summary mt-3" data-cy="battle-result-summary">
                {winner.character.name} wins - {loser.character.name} defeated
              </div>
            )}
          </Col>

          <Col xs={12} md={3} className="text-center">
            <ResultAvatar participation={right} side="right" winner={String(right.id) === String(winnerParticipationId)} />
          </Col>
        </Row>

        <section className="battle-result-net" data-cy="battle-result-net">
          <span>Your net result</span>
          <strong>Total staked {totalStaked > 0 ? `${formatPoints(totalStaked)} PTS` : ""}</strong>
          <strong>Total returned {totalStaked > 0 ? `${formatPoints(totalReturned)} PTS` : ""}</strong>
          <strong className={net != null && net >= 0 ? "text-success" : "text-danger"}>
            Net {net != null ? `${net >= 0 ? "+" : "-"}${formatPoints(Math.abs(net))} PTS` : ""}
          </strong>
        </section>

        <Row className="g-4">
          <Col xs={12} md={6}>
            <ResultBetCard
              participation={left}
              oppositeParticipation={right}
              sideLabel="Side A"
              tone="cyan"
              userBets={userBets}
              winnerParticipationId={winnerParticipationId}
            />
          </Col>
          <Col xs={12} md={6}>
            <ResultBetCard
              participation={right}
              oppositeParticipation={left}
              sideLabel="Side B"
              tone="purple"
              userBets={userBets}
              winnerParticipationId={winnerParticipationId}
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const BattleDetail = () => {
  const { battleId } = useParams();
  const [battle, setBattle] = React.useState<BattleLike | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let mounted = true;

    const loadBattle = async () => {
      if (!battleId) return;

      setLoading(true);
      setError(null);

      try {
        const response = await getBattle(battleId);
        if (mounted) {
          setBattle(withStableBattleParticipations(response as BattleLike));
        }
      } catch (e: any) {
        if (mounted) {
          setError(e?.message ?? "Failed to load battle");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadBattle();

    return () => {
      mounted = false;
    };
  }, [battleId]);

  if (loading) {
    return (
      <div className="page-content battle-result-page" data-cy="battle-detail-loading">
        <Container fluid className="d-flex justify-content-center py-5">
          <Spinner />
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content battle-result-page" data-cy="battle-detail-error">
        <Container fluid>
          <Alert color="danger">{error}</Alert>
        </Container>
      </div>
    );
  }

  if (!battle || (battle.participations ?? []).length < 2 || !battleId) {
    return null;
  }

  const normalizedStatus = String(battle.status ?? "").toLowerCase();

  if (ACTIVE_STATUSES.includes(normalizedStatus)) {
    return <Dashboard battleId={battleId} />;
  }

  if (CANCELLED_STATUSES.includes(normalizedStatus)) {
    return (
      <div className="page-content battle-result-page" data-cy="battle-detail-cancelled">
        <Container fluid>
          <Alert color="secondary">This battle was cancelled.</Alert>
        </Container>
      </div>
    );
  }

  return <BattleResultView battle={battle} />;
};

export default BattleDetail;
